import { NavLink, useLocation } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { useUiStore } from '@/store/ui-store'
import {
  LayoutDashboard, Users, Package, FileText, CreditCard,
  Receipt, BarChart3, Bot, Settings, ScrollText, Bell,
  ChevronLeft, ChevronRight, Building2, BadgeIndianRupee, Store, BookOpen, Banknote,
  UserSquare2, IndianRupee, CalendarDays
} from 'lucide-react'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'

const navItems = [
  { label: 'Dashboard',   icon: LayoutDashboard, to: '/dashboard' },
  { label: 'Customers',   icon: Users,            to: '/customers' },
  { label: 'Vendors',     icon: Store,            to: '/vendors' },
  { label: 'Products & Services', icon: Package,  to: '/products' },
  { type: 'divider' as const },
  { label: 'Invoices',    icon: FileText,         to: '/invoices' },
  { label: 'Receipts',    icon: CreditCard,       to: '/payments' },
  { label: 'Expenses',    icon: Receipt,          to: '/expenses' },
  { label: 'Payments',    icon: Banknote,         to: '/expense-payments' },
  { label: 'Referrals',   icon: BadgeIndianRupee, to: '/referrals' },
  { type: 'divider' as const },
  { label: 'Employees',   icon: UserSquare2,      to: '/employees' },
  { label: 'Payroll',     icon: IndianRupee,      to: '/payroll' },
  { label: 'Leaves',      icon: CalendarDays,     to: '/leaves' },
  { type: 'divider' as const },
  { label: 'Ledgers',     icon: BookOpen,         to: '/ledgers' },
  { label: 'Reports',     icon: BarChart3,        to: '/reports' },
  { label: 'AI Assistant',icon: Bot,              to: '/ai' },
  { type: 'divider' as const },
  { label: 'Documents',   icon: ScrollText,       to: '/documents' },
  { label: 'Notifications',icon: Bell,            to: '/notifications' },
]

const bottomItems = [
  { label: 'Settings',    icon: Settings,         to: '/settings' },
  { label: 'Audit Logs',  icon: ScrollText,       to: '/audit-logs' },
]

export function Sidebar() {
  const { sidebarCollapsed, toggleSidebar } = useUiStore()
  const location = useLocation()

  return (
    <TooltipProvider delayDuration={0}>
      <aside
        className={cn(
          'flex flex-col border-r border-border bg-card transition-all duration-200 ease-in-out',
          sidebarCollapsed ? 'w-[60px]' : 'w-[240px]'
        )}
      >
        {/* Logo */}
        <div className={cn(
          'flex h-16 items-center border-b border-border px-4',
          sidebarCollapsed ? 'justify-center' : 'justify-between'
        )}>
          <div className="flex items-center gap-2 min-w-0">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary text-primary-foreground text-sm font-bold">
              F
            </div>
            {!sidebarCollapsed && (
              <span className="font-semibold text-foreground truncate">FinOps Hub</span>
            )}
          </div>
          {!sidebarCollapsed && (
            <button
              onClick={toggleSidebar}
              className="rounded-md p-1 text-muted-foreground hover:bg-accent hover:text-accent-foreground"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Collapse toggle when collapsed */}
        {sidebarCollapsed && (
          <button
            onClick={toggleSidebar}
            className="mx-auto mt-2 rounded-md p-1 text-muted-foreground hover:bg-accent hover:text-accent-foreground"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        )}

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-0.5">
          {navItems.map((item, idx) => {
            if (item.type === 'divider') {
              return <div key={idx} className="my-2 border-t border-border" />
            }
            const Icon = item.icon!
            const isActive = location.pathname.startsWith(item.to!)

            const linkContent = (
              <NavLink
                to={item.to!}
                className={cn(
                  'flex items-center gap-3 rounded-md px-2 py-2 text-sm transition-colors',
                  sidebarCollapsed ? 'justify-center' : '',
                  isActive
                    ? 'bg-primary/10 text-primary font-medium'
                    : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                )}
              >
                <Icon className="h-4 w-4 shrink-0" />
                {!sidebarCollapsed && <span className="truncate">{item.label}</span>}
              </NavLink>
            )

            if (sidebarCollapsed) {
              return (
                <Tooltip key={item.to}>
                  <TooltipTrigger asChild>{linkContent}</TooltipTrigger>
                  <TooltipContent side="right">{item.label}</TooltipContent>
                </Tooltip>
              )
            }
            return <div key={item.to}>{linkContent}</div>
          })}
        </nav>

        {/* Bottom items */}
        <div className="border-t border-border py-3 px-2 space-y-0.5">
          {bottomItems.map((item) => {
            const Icon = item.icon
            const isActive = location.pathname.startsWith(item.to)
            const linkContent = (
              <NavLink
                to={item.to}
                className={cn(
                  'flex items-center gap-3 rounded-md px-2 py-2 text-sm transition-colors',
                  sidebarCollapsed ? 'justify-center' : '',
                  isActive
                    ? 'bg-primary/10 text-primary font-medium'
                    : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                )}
              >
                <Icon className="h-4 w-4 shrink-0" />
                {!sidebarCollapsed && <span className="truncate">{item.label}</span>}
              </NavLink>
            )
            if (sidebarCollapsed) {
              return (
                <Tooltip key={item.to}>
                  <TooltipTrigger asChild>{linkContent}</TooltipTrigger>
                  <TooltipContent side="right">{item.label}</TooltipContent>
                </Tooltip>
              )
            }
            return <div key={item.to}>{linkContent}</div>
          })}
        </div>

        {/* Company indicator */}
        {!sidebarCollapsed && (
          <div className="border-t border-border p-3">
            <div className="flex items-center gap-2 rounded-md px-2 py-1.5">
              <Building2 className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
              <span className="text-xs text-muted-foreground truncate">Select a company</span>
            </div>
          </div>
        )}
      </aside>
    </TooltipProvider>
  )
}
